import React from "react";
import { Routes as RouterRoutes, Route, useLocation, Navigate, Outlet } from "react-router-dom";
import { DocumentProvider } from "@contexts/Document.context";
import LoadingOverlay from "@view/LoadingOverlay";
import { LoadingProvider } from "@contexts/Loading.context";
import { AuthenticationClientProvider } from "@contexts/AuthenticationClient.context";
import { DocumentListProvider } from "@contexts/DocumentList.context";
import { LifecycleClientProvider } from "@contexts/LifecycleClient.context";
import { JwtProvider, useJwtAuthRead, useJwtStateHandles } from "@contexts/Jwt.context";
import { ContentClientProvider } from "@contexts/ContentClient.context";
import { PaymentClientProvider } from "@contexts/PaymentClient.context";
import { AccountClientProvider } from "@contexts/AccountClient.context";
import { ClientProjectClientProvider } from "@contexts/ClientProjectClient.context";
import { AdminUserClientProvider } from "@contexts/AdminUserClient.context";
import { AdminPlanClientProvider } from "@contexts/AdminPlanClient.context";
import { OrganizationSignupClientProvider } from "@contexts/OrganizationSignupClient.context";
import { FeedbackClientProvider } from "@contexts/FeedbackClient.context";
import { EmailValidationClientProvider } from "@contexts/EmailValidationClient.context";
import { InviteAcceptClientProvider } from "@contexts/InviteAcceptClient.context";
import { StatementClassifierClientProvider } from "@contexts/StatementClassifierClient.context";
import { SdeWorkbookClientProvider } from "@contexts/SdeWorkbookClient.context";
import { DocumentAssistantRetrievalContextPreviewClientProvider } from "@contexts/DocumentAssistantRetrievalContextPreviewClient.context";
import { DocumentAssistantSupplyClientProvider } from "@contexts/DocumentAssistantSupplyClient.context";
import { DocumentAssistantDictationClientProvider } from "@contexts/DocumentAssistantDictationClient.context";
import { ListingInferenceReviewPortalClientProvider } from "@contexts/ListingInferenceReviewPortalClient.context";
import AppToaster from "@components/ui/toaster";
import {
  contextPreviewConsoleRoutePattern,
  listingInferenceReviewPortalRoutePattern,
  projectPageCounterRoutePattern,
  renderRoute,
  routeAccount,
  routeAdminPlan,
  routeAdminUsers,
  routeChooseSubscription,
  routeClients,
  routeCreateAccount,
  routeDocumentCreation,
  routeDocumentFinalEdits,
  routeDocumentRefine,
  routeDocumentSelection,
  routeEmailVerify,
  routeFeedback,
  routeHome,
  routeInviteAccept,
  routeLogin,
  routePasswordResetComplete,
  routePasswordResetRequest,
  routePageCounter,
  routeReview,
  routeStatementClassifier,
} from "@navigation/typedRoutes";
import { Routes } from "@navigation/routes";
import { enabledFeatures, FeaturesEnum } from "@utils/enabledFeatures";
import { unwrap as unwrapJwtSubscriptionStatus } from "@ps-bridge/models/brands/jwtSubscriptionStatus";
import DocumentSelectionPage from "@pages/workspace/DocumentSelection.page";
import ClientsPage from "@pages/workspace/Clients.page";
import DocumentCreationPage from "@pages/workspace/DocumentCreation.page";
import PageCounterPage from "@pages/workspace/PageCounter.page";
import FeedbackSubmissionPage from "@pages/workspace/FeedbackSubmission.page";
import AccountManagementPage from "@pages/workspace/AccountManagement.page";
import LoginPage from "@pages/account/Login.page";
import NotFoundPage from "@pages/NotFound.page";
import CreateAccountPage from "@pages/account/CreateAccount.page";
import PasswordResetRequestPage from "@pages/account/PasswordResetRequest.page";
import PasswordResetCompletePage from "@pages/account/PasswordResetComplete.page";
import EmailValidationPage from "@pages/account/EmailValidation.page";
import DocumentReviewPage from "@pages/document/Review.page";
import DocumentPromptPage from "@pages/document/Prompt.page";
import DocumentRefinePage from "@pages/document/Refine.page";
import DocumentFinalEditsPage from "@pages/document/FinalEdits.page";
import DocumentDetailsPromptPage from "@pages/document/DocumentDetailsPrompt.page";
import ChooseSubscriptionPage from "@pages/payment/ChooseSubscription.page";
import PaymentSetupPage from "@pages/payment/PaymentSetup.page";
import DemographicSurveyPage from "@pages/demographic/DemographicSurvey.page";
import AdminUsersPage from "@pages/workspace/AdminUsers.page";
import AdminPlanPage from "@pages/workspace/AdminPlan.page";
import InviteAcceptPage from "@pages/invite/InviteAccept.page";
import SdeWorkbookPage from "@pages/tax/SdeWorkbook.page";
import ContextPreviewConsolePage from "@pages/tools/ContextPreviewConsole.page";
import StatementClassifierPage from "@pages/tools/StatementClassifier.page";
import ListingInferenceReviewPortalPage from "@pages/listing/inference/ListingInferenceReviewPortal.page";
import WorkspaceSidebar from "@view/workspace/WorkspaceSidebar";
import * as styles from "@styles/AppShell.css";
import { getPendingEmailValidation } from "@src/purescript/interop";
import { renderWhen } from "@view/render";
import EmptyView from "@view/EmptyView";
import * as Option from "@utils/Option";
import { PageCounterClientProvider } from "@contexts/PageCounterClient.context";

const buildVersion = import.meta.env.VITE_APP_VERSION ?? "dev";
const buildTimestamp = import.meta.env.VITE_BUILD_TIME;
const shortBuildVersion =
  buildVersion.length > 12 && /^[a-f0-9]+$/i.test(buildVersion)
    ? buildVersion.slice(0, 12)
    : buildVersion;
const buildBadgeLabel = buildTimestamp
  ? `Build ${shortBuildVersion} (${buildTimestamp})`
  : `Build ${shortBuildVersion}`;

const PublicLayout: React.FC = () => (
  <div className={styles.shell}>
    <main className={styles.main} data-testid="public-layout-main">
      <Outlet />
    </main>
    <div className={styles.buildVersionBadge} data-testid="build-version-badge">
      {buildBadgeLabel}
    </div>
  </div>
);

const ProtectedLayout: React.FC = () => {
  const location = useLocation();
  const showSidebar = location.pathname.startsWith("/workspace") || location.pathname.startsWith("/document") || location.pathname.startsWith("/tools");
  const pendingEmailValidation = getPendingEmailValidation();
  const pendingBannerView = renderWhen(
    showSidebar && Boolean(pendingEmailValidation),
    <div className={styles.pendingBanner}>
      <div>
        <strong>Pending email validation.</strong> Check your inbox to unlock the full workspace.
      </div>
      <div className={styles.pendingBannerHint}>
        You can invite users while validation is pending.
      </div>
    </div>
  );

  return (
    <div className={styles.shell}>
      <WorkspaceSidebar />
      <main
        className={styles.mainWithSidebar}
        data-testid="app-shell-main"
      >
        {pendingBannerView}
        <Outlet />
      </main>
      <div className={styles.buildVersionBadge} data-testid="build-version-badge">
        {buildBadgeLabel}
      </div>
    </div>
  );
};

const RequireAuth: React.FC = () => {
  const location = useLocation();
  const { isTokenExpiring, subscriptionStatus } = useJwtStateHandles();
  const hasCheckedAuth = useJwtAuthRead();
  const stubEnabled = enabledFeatures[FeaturesEnum.StubClients];
  const authorized =
    !isTokenExpiring.read && unwrapJwtSubscriptionStatus(subscriptionStatus.read) !== "not-found";
  const pendingEmailValidation = getPendingEmailValidation();

  if (!hasCheckedAuth) {
    return <EmptyView />;
  }

  if (stubEnabled || authorized) {
    const pendingEmailAllowedPaths = [
      renderRoute(routeAdminUsers),
      renderRoute(routePageCounter),
    ];
    if (pendingEmailValidation && !pendingEmailAllowedPaths.includes(location.pathname)) {
      return <Navigate to={renderRoute(routeAdminUsers)} replace state={{ from: location }} />;
    }
    return <Outlet />;
  }

  const nextPath = `${location.pathname}${location.search}${location.hash}`;
  const loginWithRedirect = `${renderRoute(routeLogin)}?next=${encodeURIComponent(nextPath)}`;
  return <Navigate to={loginWithRedirect} replace />;
};

const App: React.FC = () => {
  const spinnerVariant = enabledFeatures[FeaturesEnum.SexySpinner] ? "sexy" : "fallback";

  return (
    <LoadingProvider>
      <AuthenticationClientProvider>
        <JwtProvider>
          <LifecycleClientProvider>
            <ContentClientProvider>
              <PaymentClientProvider>
                <AccountClientProvider>
                  <AdminUserClientProvider>
                    <AdminPlanClientProvider>
                      <OrganizationSignupClientProvider>
                        <FeedbackClientProvider>
                          <EmailValidationClientProvider>
                            <InviteAcceptClientProvider>
                                <StatementClassifierClientProvider client={Option.none()}>
                                <SdeWorkbookClientProvider>
                                <DocumentAssistantRetrievalContextPreviewClientProvider>
                                <DocumentAssistantSupplyClientProvider mode="default">
                                <DocumentAssistantDictationClientProvider client={Option.none()}>
                                <ClientProjectClientProvider>
                                <DocumentProvider>
                                  <DocumentListProvider>
                                <RouterRoutes>
                                <Route element={<PublicLayout />}>
                                  <Route path={renderRoute(routeLogin)} element={<LoginPage />} />
                                  <Route path={renderRoute(routeCreateAccount)} element={<CreateAccountPage />} />
                                  <Route path={renderRoute(routePasswordResetRequest)} element={<PasswordResetRequestPage />} />
                                  <Route path={renderRoute(routePasswordResetComplete)} element={<PasswordResetCompletePage />} />
                                  <Route path={renderRoute(routeEmailVerify)} element={<EmailValidationPage />} />
                                  <Route path={renderRoute(routeInviteAccept)} element={<InviteAcceptPage />} />
                                </Route>
                                <Route element={<RequireAuth />}>
                                  <Route element={<ProtectedLayout />}>
                                    <Route path={renderRoute(routeHome)} element={<Navigate to={renderRoute(routeClients)} replace />} />
                                    <Route path={renderRoute(routeDocumentSelection)} element={<DocumentSelectionPage />} />
                                    <Route path={renderRoute(routeClients)} element={<ClientsPage />} />
                                    <Route path={`${renderRoute(routeClients)}/:clientId`} element={<ClientsPage />} />
                                    <Route path={`${renderRoute(routeClients)}/:clientId/projects/:projectId`} element={<ClientsPage />} />
                                    <Route path={renderRoute(routeDocumentCreation)} element={<DocumentCreationPage />} />
                                    <Route
                                      path={renderRoute(routePageCounter)}
                                      element={
                                        <PageCounterClientProvider client={Option.none()}>
                                          <PageCounterPage />
                                        </PageCounterClientProvider>
                                      }
                                    />
                                    <Route
                                      path={projectPageCounterRoutePattern()}
                                      element={
                                        <PageCounterClientProvider client={Option.none()}>
                                          <PageCounterPage />
                                        </PageCounterClientProvider>
                                      }
                                    />
                                    <Route path={renderRoute(routeFeedback)} element={<FeedbackSubmissionPage />} />
                                    <Route path={Routes.sdeWorkbook} element={<SdeWorkbookPage />} />
                                    <Route
                                      path={renderRoute(routeStatementClassifier)}
                                      element={
                                        <StatementClassifierPage
                                          featureEnabled={enabledFeatures[FeaturesEnum.StatementClassifier]}
                                          pollingOverrides={Option.none()}
                                        />
                                      }
                                    />
                                    <Route
                                      path={contextPreviewConsoleRoutePattern()}
                                      element={<ContextPreviewConsolePage />}
                                    />
                                    <Route
                                      path={listingInferenceReviewPortalRoutePattern()}
                                      element={
                                        <ListingInferenceReviewPortalClientProvider client={Option.none()}>
                                          <ListingInferenceReviewPortalPage />
                                        </ListingInferenceReviewPortalClientProvider>
                                      }
                                    />
                                    <Route path={renderRoute(routeAccount)} element={<AccountManagementPage />} />
                                    <Route path={renderRoute(routeAdminUsers)} element={<AdminUsersPage />} />
                                    <Route path={renderRoute(routeAdminPlan)} element={<AdminPlanPage />} />
                                    <Route path={`${renderRoute(routeReview)}/:documentId?`} element={<DocumentReviewPage />} />
                                    <Route path={Routes.prompt} element={<DocumentPromptPage />} />
                                    <Route path={renderRoute(routeDocumentRefine)} element={<DocumentRefinePage />} />
                                    <Route path={renderRoute(routeDocumentFinalEdits)} element={<DocumentFinalEditsPage />} />
                                    <Route path={Routes.documentDetailsPrompt} element={<DocumentDetailsPromptPage />} />
                                    <Route path={renderRoute(routeChooseSubscription)} element={<ChooseSubscriptionPage />} />
                                    <Route path={Routes.paymentSetup} element={<PaymentSetupPage />} />
                                    <Route path={Routes.demographicSurvey} element={<DemographicSurveyPage />} />
                                  </Route>
                                </Route>
                                <Route path="*" element={<NotFoundPage />} />
                                </RouterRoutes>
                                <LoadingOverlay spinnerVariant={spinnerVariant} />
                                  </DocumentListProvider>
                                </DocumentProvider>
                                </ClientProjectClientProvider>
                                </DocumentAssistantDictationClientProvider>
                                </DocumentAssistantSupplyClientProvider>
                                </DocumentAssistantRetrievalContextPreviewClientProvider>
                                </SdeWorkbookClientProvider>
                                </StatementClassifierClientProvider>
                            </InviteAcceptClientProvider>
                          </EmailValidationClientProvider>
                        </FeedbackClientProvider>
                      </OrganizationSignupClientProvider>
                    </AdminPlanClientProvider>
                  </AdminUserClientProvider>
                </AccountClientProvider>
              </PaymentClientProvider>
            </ContentClientProvider>
          </LifecycleClientProvider>
        </JwtProvider>
      </AuthenticationClientProvider>
      <AppToaster />
    </LoadingProvider>
  );
};

export default App;
